import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ReactFlowProvider } from 'reactflow';
import { useApolloClient } from '@apollo/client';
import { AutomationStatus, AutomationActionNodeSubType, AutomationProvidersEnum } from '@frontend/app/types/globalTypes';
import {
  useCreateAutomationMutation,
  useCreateRevisionMutation,
  useGetAutomationQuery,
  useGetTemplateQuery,
  useGetNodeOptionsQuery,
  useDisableAutomationMutation,
  useResumeAutomationMutation,
  useDeleteAutomationMutation,
} from '@frontend/app/hooks/automations';
import {
  useUpdateMessageTemplateMutation,
  useCreateMessageTemplateMutation,
  MessageTemplate,
  MessageTemplateInput
} from '@frontend/app/hooks';
import { useGetProgramById } from '@frontend/app/hooks';
import omitDeep from 'omit-deep-lodash';

import { AutomationNodeType } from '@frontend/app/types/globalTypes';
import { TemplateType } from '@frontend/app/types/globalTypes';
import { map, isNumber } from 'lodash';
import { EventName, logger } from '@common';
import { useEventContext } from '@frontend/app/context/EventContext';
import { useHistory } from 'react-router-dom';
import { useCreateDefaultAutomationMessageTemplates } from '@frontend/app/hooks/automations/useCreateDefaultAutomationMessageTemplates';
import { Sheet, SheetContent } from '@/shadcn/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shadcn/components/ui/alert-dialog';
import { AutomationHeader } from './AutomationHeader';
import { FlowCanvas } from './FlowCanvas';
import { FloatingNodePalette } from './FloatingNodePalette';
import { PropertyPanel } from './PropertyPanel';
import { TemplateSelectionModal } from './TemplateSelectionModal';
import {
  AutomationNodeV2,
  AutomationEditorState,
  AutomationEditorActions,
  NodeTemplate,
} from './types';
import {
  findNodeById,
  updateNode,
  deleteNodeWithChildPreservation,
  deletePath,
  moveNode,
  cloneNode,
  generateNodeId,
  validateAutomationTree,
  getAllNodes,
  convertTemplateToV2Blueprint,
  ensureNodeIds,
  insertNodeInline,
  addBranchNode,
  replacePlaceholderNode,
  ensureConditionSubTypes,
  addNodeToHiddenRoot,
  ensureHiddenRootConfiguration,
  updateNodesWithNewTemplateId
} from './utils';
import { useMessagingContext } from '@/hooks';
import { useAutomationCommunicationStandalone } from './hooks';
import { DEFAULT_ACTIONS } from './constants';

interface AutomationBuilderV2Props {
  automationId?: string;
  templateId?: string;
  projectId: number;
  onSave?: () => void;
  onCancel?: () => void;
  readOnly?: boolean;
  // Breadcrumb configuration
  breadcrumbs?: Array<{
    label: string;
    href?: string;
    onClick?: () => void;
  }>;
  currentPage?: string; // e.g., "Create Automation", "Edit Automation"
  isStaOffer?: boolean;
  redirectUrl?: string;
}

export const AutomationBuilderV2: React.FC<AutomationBuilderV2Props> = ({
  automationId,
  templateId: initialTemplateId,
  projectId,
  onSave,
  onCancel,
  readOnly = false,
  breadcrumbs = [],
  currentPage,
  isStaOffer = false,
  redirectUrl,
}) => {
  // Hooks
  const apolloClient = useApolloClient();
  const { showSuccessMessage, showErrorMessage } = useMessagingContext();
  const [createAutomation, { loading: isCreating }] = useCreateAutomationMutation();
  const [createRevision, { loading: isUpdating }] = useCreateRevisionMutation();
  const [updateMessageTemplate] = useUpdateMessageTemplateMutation();
  const [createMessageTemplate] = useCreateMessageTemplateMutation();
  const addEvent = useEventContext();
  const [disableAutomation, { loading: disableLoading }] = useDisableAutomationMutation();
  const [resumeAutomation, { loading: resumeLoading }] = useResumeAutomationMutation();
  const [deleteAutomation, { loading: deleteLoading }] = useDeleteAutomationMutation();
  const [mutateFunction, { loading: isLoadingMessageTemplates }] = useCreateDefaultAutomationMessageTemplates();
  const { sendBlueprintToAVA, onBlueprintEvent } = useAutomationCommunicationStandalone({
    projectId,
    onBlueprintReceived: (_blueprint) => {
      // TODO: Implement blueprint received from AVA
    },
    onBlueprintSent: (_blueprint) => {
      // TODO: Implement blueprint sent to AVA
    },
  });
  const history = useHistory();

  // State for template selection
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(initialTemplateId || null);
  const [showTemplateSelection, setShowTemplateSelection] = useState<boolean>(!automationId && !initialTemplateId);

  // State for message template updates (like V1 system)
  const [updatedTemplate, setUpdatedTemplate] = useState<MessageTemplate | MessageTemplateInput>(null);

  // Track original template data for comparison
  const [originalTemplateData, setOriginalTemplateData] = useState<Record<string, any>>({});

  // State for automation action dialogs
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteNodeDialog, setShowDeleteNodeDialog] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<string | null>(null);

  const {
    data: { automation } = { automation: null },
    loading: isLoadingAutomation,
    refetch: refetchAutomation,
  } = useGetAutomationQuery({
    variables: { automationId, isAutomationV2: true },
    skip: !automationId,
  });

  // Listen for blueprint events from AVA
  useEffect(() => {
    const unsubscribe = onBlueprintEvent((event) => {
      if (event.type === 'BLUEPRINT_IMPORTED' && event.payload.blueprint) {
        // Import blueprint from AVA
        const importedBlueprint = event.payload.blueprint;

        // Update the editor state with the imported blueprint
        setEditorState((prev) => ({
          ...prev,
          blueprint: {
            ...importedBlueprint,
            programId: projectId, // Ensure projectId is set correctly
          },
          selectedNodeId: null,
          hasUnsavedChanges: true, // Mark as having unsaved changes
        }));

        showSuccessMessage('Blueprint imported from AVA successfully');
      }
    });

    return unsubscribe;
  }, [onBlueprintEvent, showSuccessMessage, projectId]);

  // Load template data

  const {
    data,
    loading: isLoadingTemplate,
    refetch: refetchTemplate,
  } = useGetTemplateQuery({
    variables: {
      templateId: selectedTemplateId || '',
      ...((!isStaOffer) && {
        context: {
          programId: projectId,
          isAutomationV2: true, // Always true for AutomationV2
        },
      }),
    },
    skip: !selectedTemplateId,
  });

  const template = data?.template?.blueprint;

  // Load node options (action and condition options)
  const {
    data: {
      getNodeOptions: { actionOptions = [], conditionOptions = [] } = { actionOptions: [], conditionOptions: [] },
    } = { getNodeOptions: { actionOptions: [], conditionOptions: [] } },
    loading: isLoadingNodeOptions,
    refetch: refetchNodeOptions,
  } = useGetNodeOptionsQuery({
    variables: {
      context: {
        automationId,
        templateId: selectedTemplateId,
        ...(isStaOffer && { providerName: AutomationProvidersEnum.STA_OFFER_EMAILS }),
      },
    },
    skip: !selectedTemplateId && !automationId,
  });

  // Prevent duplicate initialization in StrictMode/dev
  const initRef = React.useRef(false);
  // Initialize default automation message templates (same as V1)
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      try {
        const response = await mutateFunction();
        if (response?.data?.messageTemplates?.length > 0) {
          await Promise.all([refetchNodeOptions(), refetchTemplate(), refetchAutomation()]);
        }
      } catch (e) {
        logger.error(e);
      }
    })();
    // Run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize state with empty blueprint
  // When editing existing automation, start in view mode (not editing)
  // When creating new automation, start in edit mode
  const [editorState, setEditorState] = useState<AutomationEditorState>({
    blueprint: {
      id: automationId,
      name: '',
      description: '',
      triggers: [],
      programId: projectId,
      templateId: selectedTemplateId,
      metadata: { icon: 'workflow' }
    },
    selectedNodeId: null,
    isEditing: !readOnly && !automationId, // Edit mode for new automations, view mode for existing
    hasUnsavedChanges: false,
    draggedNodeType: null,
    clipboard: null,
  });

  // Effect to initialize blueprint from template
  useEffect(() => {
    if (template && selectedTemplateId && !automationId) {
      try {
        // Convert the template blueprint to V2 structure with proper node rendering
        const convertedBlueprint = convertTemplateToV2Blueprint(template, projectId);

        // Ensure root node is auto-configured with 1ms delay
        const configuredBlueprint = {
          ...convertedBlueprint,
          triggers: convertedBlueprint.triggers.map((trigger) => ({
            ...trigger,
            root: ensureHiddenRootConfiguration(trigger.root),
          })),
        };

        setEditorState((prev) => ({
          ...prev,
          blueprint: configuredBlueprint,
          selectedNodeId: null,
          hasUnsavedChanges: false,
        }));
      } catch (error) {
        showErrorMessage('Failed to load template. Please try again.');

        // Fallback to empty blueprint
        setEditorState((prev) => ({
          ...prev,
          blueprint: {
            id: undefined,
            name: template.name || 'Untitled Automation',
            description: template.description || '',
            triggers: [],
            programId: projectId,
            templateId: selectedTemplateId,
            metadata: template.metadata || { icon: 'workflow' }
          },
          selectedNodeId: null,
        }));
      }
    }
  }, [template, selectedTemplateId, projectId, showErrorMessage, automationId]);
  // Effect to initialize blueprint from automation (simplified for UI testing)
  useEffect(() => {
    if (automation) {
      // Loading existing automation
      const automationBlueprint = omitDeep(automation, '__typename');

      // Ensure root nodes are auto-configured with 1ms delay
      const configuredTriggers = automationBlueprint.triggers.map((trigger: any) => ({
        ...trigger,
        root: trigger.root ? ensureHiddenRootConfiguration(trigger.root) : trigger.root,
      }));

      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          id: automationBlueprint.id,
          name: automationBlueprint.name,
          description: automationBlueprint.description,
          triggers: configuredTriggers,
          programId: projectId,
          templateId: automationBlueprint.templateId,
          metadata: automationBlueprint.metadata || { icon: 'workflow' }
        },
        isEditing: false, // Always start in view mode when editing existing automation
        // Select the trigger node by default
        selectedNodeId: automationBlueprint.triggers[0]?.id,
      }));
    }
  }, [automation, projectId]);

  // Template selection handlers
  const handleTemplateSelect = useCallback((templateId: string) => {
    // Update state (React 17 will batch these automatically in event handlers)
    setSelectedTemplateId(templateId);
    setShowTemplateSelection(false);
  }, []);

  const handleCancelTemplateSelection = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  // Derived state
  const currentTrigger = editorState.blueprint.triggers[0] || null;
  const selectedNode = editorState.selectedNodeId && currentTrigger?.root
    ? findNodeById(currentTrigger.root, editorState.selectedNodeId)
    : null;

  // Message template save function (like V1 system)
  const saveMessageTemplate = useCallback(async (): Promise<number | null> => {
    if (!updatedTemplate) {
      return null;
    }

    // For existing templates, check if content actually changed
    if (isNumber(updatedTemplate.id)) {
      const originalData = originalTemplateData[updatedTemplate.id];
      if (originalData) {
        const hasContentChanged = (
          originalData.title !== updatedTemplate.title ||
          originalData.subject !== updatedTemplate.subject ||
          originalData.text !== updatedTemplate.text ||
          JSON.stringify(originalData.attachments || []) !== JSON.stringify(updatedTemplate.attachments || [])
        );

        if (!hasContentChanged) {
          setUpdatedTemplate(null);
          return updatedTemplate.id as number;
        }
      }
    }

    try {
      let templateId: number;
      const preparedAttachments = map(updatedTemplate.attachments, (attachment: any) => ({
        id: attachment.id,
        name: attachment.name,
        url: attachment.fileUrl,
        size: attachment.size,
        type: attachment.type,
        uuid: attachment.uuid,
        fileUrl: attachment.fileUrl,
        localSrc: attachment.localSrc,
      }));

      const isNewTemplate = !isNumber(updatedTemplate.id);

      if (isNewTemplate) {
        const { data: { template } } = await createMessageTemplate({
          variables: {
            data: {
              title: updatedTemplate.title,
              subject: updatedTemplate.subject,
              text: updatedTemplate.text,
              type: updatedTemplate.type === TemplateType.INVITE
                ? updatedTemplate.type.toString().toLowerCase()
                : updatedTemplate.type.toString().toLowerCase() || 'generic',
              programId: !isStaOffer ? projectId : null,
              attachments: preparedAttachments,
            },
          },
        });
        templateId = template.id;
        addEvent(EventName.MessageTemplates, {
          template_id: template.id,
          template_name: updatedTemplate.title,
          action: 'create',
        });
      } else {
        const { data: { template } } = await updateMessageTemplate({
          variables: {
            id: updatedTemplate.id as number,
            data: {
              title: updatedTemplate.title,
              subject: updatedTemplate.subject,
              text: updatedTemplate.text,
              attachments: preparedAttachments,
            },
          },
        });
        templateId = template.id;
        addEvent(EventName.MessageTemplates, {
          template_id: template.id,
          template_name: updatedTemplate.title,
          action: 'save',
        });
      }

      setUpdatedTemplate(null);

      // Invalidate Apollo cache to refresh template data in UI
      try {
        await apolloClient.refetchQueries({
          include: ['GetMessageTemplateById']
        });
      } catch (cacheError) {
        console.warn('Failed to refresh template cache:', cacheError);
      }

      return templateId;
    } catch (error) {
      showErrorMessage(`Failed to save template: ${error.message || 'Unknown error'}`);
      return null;
    }
  }, [updatedTemplate, originalTemplateData, createMessageTemplate, updateMessageTemplate, projectId, addEvent, showErrorMessage, isStaOffer, apolloClient]);

  // Message template update handler (exactly like V1 system)
  const handleMessageTemplateUpdate = useCallback((template: MessageTemplate | MessageTemplateInput) => {
    // Store original template data on first load for comparison
    if (template && isNumber(template.id) && !originalTemplateData[template.id]) {
      setOriginalTemplateData((prev) => ({
        ...prev,
        [template.id]: {
          title: template.title,
          subject: template.subject,
          text: template.text,
          attachments: template.attachments || [],
        }
      }));
    }

    // Store template updates directly in state (like V1 system setUpdatedTemplate)
    // The MessageTemplateEditor will call this for any change, including initial load
    // Only save to backend when automation is saved
    setUpdatedTemplate(template);
  }, [originalTemplateData]);

  // Template refresh handler - force refresh after template updates
  const handleRefreshTemplate = useCallback(async () => {
    try {
      await apolloClient.refetchQueries({
        include: ['GetMessageTemplateById']
      });
    } catch (error) {
      console.warn('Failed to refresh template data:', error);
    }
  }, [apolloClient]);

  // Actions
  // Load program data for project validation
  const program = useGetProgramById({
    variables: {
      id: projectId,
      getWorkItems: true,
    }
  });

  const actions: AutomationEditorActions = useMemo(() => ({
    setSelectedNode: (nodeId: string | null) => {
      setEditorState((prev) => {
        // Only clear template states when switching between different nodes (not when closing panel)
        // We want to preserve template changes when property panel closes so they can be saved later
        const isSwitchingBetweenNodes = prev.selectedNodeId && nodeId && prev.selectedNodeId !== nodeId;
        if (isSwitchingBetweenNodes) {
          setUpdatedTemplate(null);
          setOriginalTemplateData({});
        }

        return {
          ...prev,
          selectedNodeId: nodeId,
        };
      });
    },

    updateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => {
      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          ...prev.blueprint,
          triggers: prev.blueprint.triggers.map((trigger) => ({
            ...trigger,
            root: updateNode(trigger.root, nodeId, updates),
          })),
        },
        hasUnsavedChanges: true,
      }));
    },

    addNode: (parentId: string, newNode: AutomationNodeV2, isElseBranch?: boolean, isBranch?: boolean) => {
      setEditorState((prev) => {
        // Check current node count before adding
        const currentTrigger = prev.blueprint.triggers[0];
        if (currentTrigger?.root) {
          const allNodes = getAllNodes(currentTrigger.root);
          if (allNodes.length >= 20) {
            showErrorMessage('Maximum of 20 nodes allowed per automation');
            return prev;
          }
        }

        return {
          ...prev,
          blueprint: {
            ...prev.blueprint,
            triggers: prev.blueprint.triggers.map((trigger) => {
              // Check if the parentId is the trigger ID - add to hidden root
              if (trigger.id === parentId) {
                // For inline insertion (non-branch), pass true; for branch addition, pass false
                return addNodeToHiddenRoot(trigger, newNode, !isBranch);
              }

              // Otherwise, use regular node insertion logic
              const updateFunction = isBranch ? addBranchNode : insertNodeInline;
              return {
                ...trigger,
                root: updateFunction(trigger.root, parentId, newNode, isElseBranch),
              };
            }),
          },
          hasUnsavedChanges: true,
          // Don't auto-select placeholder nodes to prevent PropertyPanel from opening
          selectedNodeId: (newNode.metadata?.isPlaceholder || newNode.metadata?.isActionPlaceholder) ? null : newNode.id,
        };
      });
    },

    replacePlaceholder: (placeholderId: string, newNode: AutomationNodeV2) => {
      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          ...prev.blueprint,
          triggers: prev.blueprint.triggers.map((trigger) => ({
            ...trigger,
            root: replacePlaceholderNode(trigger.root, placeholderId, newNode),
          })),
        },
        hasUnsavedChanges: true,
        selectedNodeId: newNode.id,
      }));
    },

    deleteNode: (nodeId: string) => {
      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          ...prev.blueprint,
          triggers: prev.blueprint.triggers.map((trigger) => ({
            ...trigger,
            root: deleteNodeWithChildPreservation(trigger.root, nodeId) || trigger.root,
          })),
        },
        hasUnsavedChanges: true,
        selectedNodeId: prev.selectedNodeId === nodeId ? null : prev.selectedNodeId,
      }));
    },

    deletePath: (nodeId: string) => {
      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          ...prev.blueprint,
          triggers: prev.blueprint.triggers.map((trigger) => ({
            ...trigger,
            root: deletePath(trigger.root, nodeId) || trigger.root,
          })),
        },
        hasUnsavedChanges: true,
        selectedNodeId: prev.selectedNodeId === nodeId ? null : prev.selectedNodeId,
      }));
    },

    moveNode: (nodeId: string, newParentId: string, index?: number) => {
      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          ...prev.blueprint,
          triggers: prev.blueprint.triggers.map((trigger) => ({
            ...trigger,
            root: moveNode(trigger.root, nodeId, newParentId, index),
          })),
        },
        hasUnsavedChanges: true,
      }));
    },

    copyNode: (nodeId: string) => {
      const nodeToCopy = findNodeById(currentTrigger.root, nodeId);
      if (nodeToCopy) {
        setEditorState((prev) => ({
          ...prev,
          clipboard: cloneNode(nodeToCopy, false), // Don't generate new IDs for clipboard
        }));
      }
    },

    pasteNode: (parentId: string) => {
      if (editorState.clipboard) {
        const clonedNode = cloneNode(editorState.clipboard, true); // Generate new IDs when pasting
        actions.addNode(parentId, clonedNode);
      }
    },

    setEditMode: (editing: boolean) => {
      setEditorState((prev) => ({
        ...prev,
        isEditing: editing,
      }));
    },

    saveBlueprint: async () => {
      // Save message template first if there are changes
      let savedTemplateId: number | null = null;
      if (updatedTemplate) {
        savedTemplateId = await saveMessageTemplate();

        if (savedTemplateId === null) {
          return; // Template save failed, don't proceed with automation save
        }

        // Update any nodes that have templateId: null with the newly created template ID
        if (savedTemplateId && !isNumber(updatedTemplate.id)) {
          // Update the editor state AND store the updated blueprint for immediate use
          const updatedBlueprint = {
            ...editorState.blueprint,
            triggers: editorState.blueprint.triggers.map((trigger) => ({
              ...trigger,
              root: updateNodesWithNewTemplateId(trigger.root, savedTemplateId, {
                title: updatedTemplate.title,
                subject: updatedTemplate.subject,
              }),
            })),
          };

          setEditorState((prev) => ({
            ...prev,
            blueprint: updatedBlueprint,
          }));

          // Use the updated blueprint for the rest of the function
          editorState.blueprint = updatedBlueprint;
        }

        // Refresh both template data and node options (includes template list) in UI after successful save
        // Add small delay to ensure backend has processed the new template
        await new Promise((resolve) => setTimeout(resolve, 100));
        await Promise.all([
          handleRefreshTemplate(),
          refetchNodeOptions()
        ]);
      }

      // Prepare the blueprint for API following the existing pattern
      const blueprintForAPI = automationId ? {
        // For updates - use blueprintInput format
        id: editorState.blueprint.id,
        name: editorState.blueprint.name,
        description: editorState.blueprint.description,
        programId: isStaOffer ? null : projectId,
        providerId: isStaOffer ? projectId : null,
        providerName: isStaOffer ? AutomationProvidersEnum.STA_OFFER_EMAILS : null,
        metadata: editorState.blueprint.metadata,
        triggers: editorState.blueprint.triggers.map((trigger) => ({
          ...trigger,
          root: ensureNodeIds(ensureConditionSubTypes(ensureHiddenRootConfiguration({
            ...trigger.root,
            metadata: (() => {
              const metadata = {
                ...trigger.root.metadata,
                brandCatalogId: trigger.root.metadata?.brandCatalogId,
              };
              // Only include selectionRuleId if it exists and is not null
              if (trigger.root.metadata?.selectionRuleId !== null && trigger.root.metadata?.selectionRuleId !== undefined) {
                metadata.selectionRuleId = trigger.root.metadata.selectionRuleId;
              }
              return metadata;
            })()
          })))
        })),
      } : {
        // For creation - use full blueprint format
        ...editorState.blueprint,
        programId: isStaOffer ? null : projectId,
        providerId: isStaOffer ? projectId : null,
        providerName: isStaOffer ? AutomationProvidersEnum.STA_OFFER_EMAILS : null,
        triggers: editorState.blueprint.triggers.map((trigger) => ({
          ...trigger,
          root: ensureNodeIds(ensureConditionSubTypes(ensureHiddenRootConfiguration({
            ...trigger.root,
            metadata: (() => {
              const metadata = {
                ...trigger.root.metadata,
                brandCatalogId: trigger.root.metadata?.brandCatalogId,
              };
              // Only include selectionRuleId if it exists and is not null
              if (trigger.root.metadata?.selectionRuleId !== null && trigger.root.metadata?.selectionRuleId !== undefined) {
                metadata.selectionRuleId = trigger.root.metadata.selectionRuleId;
              }
              return metadata;
            })()
          })))
        })),
      };

      try {
        // Use the updated blueprint for validation (not the potentially stale currentTrigger)
        const triggerToValidate = editorState.blueprint.triggers[0];
        if (!triggerToValidate?.root) {
          showErrorMessage('No automation trigger found. Please select a template first.');
          return;
        }

        const errors = validateAutomationTree(triggerToValidate.root);

        // Additional validation for API requirements
        const apiValidationErrors: string[] = [];
        const allNodes = getAllNodes(triggerToValidate.root);

        // Check maximum node count
        if (allNodes.length > 20) {
          showErrorMessage('Maximum of 20 nodes allowed per automation');
          return;
        }

        allNodes.forEach((node) => {
          if (node.type === AutomationNodeType.ACTION) {
            if (!node.metadata.resourceId && !node.metadata.fromPreviousSender) {
              apiValidationErrors.push('Please select an email');
            }
            if (node.metadata.fromPreviousSender === undefined) {
              apiValidationErrors.push('Action node fromPreviousSender is required');
            }
            if (node.subType === AutomationActionNodeSubType.SEND_EMAIL && !node.metadata.templateId) {
              apiValidationErrors.push('Please select an email template');
            }
            // Validate UPDATE_MEMBER_DATA requirements
            if (node.subType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA && node.metadata) {
              const updateType = node.metadata.type;
              const fieldValue = node.metadata.fieldValue;
              const values = node.metadata.values || [];
              const customFields = node.metadata.customFields || [];

              if (!updateType) {
                apiValidationErrors.push('Please select an update type');
              } else if (updateType === 'ADD_TAG' && (!fieldValue || fieldValue === '') && values.length === 0) {
                apiValidationErrors.push('Please select at least one tag');
              } else if (updateType === 'ADD_TO_GROUP' && (!fieldValue || fieldValue === '') && values.length === 0) {
                apiValidationErrors.push('Please select at least one group');
              } else if (updateType === 'UPDATE_MEMBER_INFORMATION' && values.length === 0 && customFields.length === 0) {
                apiValidationErrors.push('Please configure at least one custom field');
              } else if (updateType === 'SEND_EMAIL' && values.length === 0 && customFields.length === 0) {
                apiValidationErrors.push('Please Select an email template');
              }
            }
          }

          if (node.type === AutomationNodeType.CONDITION) {
            if (!node.metadata.condition) {
              apiValidationErrors.push('Validation Failed: You have not configured the conditions');
            }
          }

          if (node.type === AutomationNodeType.DELAY) {
            if (!node.metadata.durationMs || node.metadata.durationMs <= 0) {
              apiValidationErrors.push('Delay time/duration must be greater than 0');
            }
          }
        });

        const allValidationErrors = [...errors, ...apiValidationErrors];
        if (allValidationErrors.length > 0) {
          showErrorMessage(`Validation errors : ${allValidationErrors.join('; ')}`);
          return;
        }

        if (automationId) {
          // Update existing automation
          await createRevision({
            variables: {
              blueprint: blueprintForAPI,
              options: {
                publish: automation?.status === AutomationStatus.ACTIVE,
              },
            },
            onCompleted() {
              refetchAutomation();
              showSuccessMessage('Successfully updated automation');
            },
            onError(error) {
              // Handle errors gracefully like the existing system
              error.graphQLErrors = [];
              throw error;
            },
          });
        } else {
          // Create new automation
          await createAutomation({
            variables: {
              blueprint: blueprintForAPI,
              options: {
                publish: true,
              },
            },
            onCompleted() {
              refetchAutomation();
              showSuccessMessage('Successfully created automation');
            },
            onError(error) {
              error.graphQLErrors = [];
              throw error;
            },
          });

          if (onSave) {
            onSave();
          }
          // Send updated blueprint to AVA
          sendBlueprintToAVA(editorState.blueprint, {
            source: 'AUTOMATION_BUILDER',
            timestamp: Date.now(),
            action: 'saved'
          });
        }

        setEditorState((prev) => ({
          ...prev,
          hasUnsavedChanges: false,
        }));

        // Clear any remaining template state after successful save
        setUpdatedTemplate(null);
        setOriginalTemplateData({});
      } catch (error) {
        let errorMessage = 'Failed to save automation';
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          errorMessage = error.graphQLErrors.map((err) => err.message).join('; ');
        } else if (error.networkError) {
          errorMessage = `Network error: ${error.networkError.message}`;
        } else if (error.message) {
          errorMessage = error.message;
        }

        showErrorMessage(errorMessage);
      }
    },
  }), [
    refetchNodeOptions,
    editorState,
    isStaOffer,
    currentTrigger?.root,
    automationId,
    projectId,
    automation?.status,
    createRevision,
    createAutomation,
    showSuccessMessage,
    showErrorMessage,
    onSave,
    refetchAutomation,
    sendBlueprintToAVA,
    updatedTemplate,
    saveMessageTemplate,
    handleRefreshTemplate,
  ]);

  // Event handlers
  const handleNodeClick = useCallback((nodeId: string) => {
    actions.setSelectedNode(nodeId);
  }, [actions]);

  const handleUpdateNode = useCallback((nodeId: string, updates: Partial<AutomationNodeV2>) => {
    actions.updateNode(nodeId, updates);
  }, [actions]);

  const handleAddChild = useCallback((parentId: string, template: NodeTemplate, isElseBranch?: boolean, isBranch?: boolean) => {
    // Ensure proper default metadata based on node type
    let defaultMetadata = { ...template.defaultMetadata };

    if (template.type === 'ACTION') {
      defaultMetadata = {
        ...defaultMetadata,
        resourceId: DEFAULT_ACTIONS.RESOURCE_ID,
        fromPreviousSender: false,
        templateId: template.subType === AutomationActionNodeSubType.SEND_EMAIL
          ? DEFAULT_ACTIONS.DEFAULT_EMAIL_TEMPLATE_ID
          : undefined,
      };
    } else if (template.type === 'CONDITION') {
      defaultMetadata = {
        ...defaultMetadata,
        condition: null // Start with empty condition - user must configure
      };
    } else if (template.type === 'DELAY') {
      // No default metadata for delay nodes - let users configure them
      defaultMetadata = {
        ...defaultMetadata,
      };
    }

    const newNode: AutomationNodeV2 = {
      id: generateNodeId(),
      type: template.type,
      subType: template.subType,
      children: [],
      elseChildren: template.type === 'CONDITION' ? [] : undefined,
      metadata: defaultMetadata,
      variables: [],
    };

    // Pass isElseBranch and isBranch to actions.addNode
    actions.addNode(parentId, newNode, isElseBranch, isBranch);
  }, [actions]);

  const handleDeleteNode = useCallback((nodeId: string) => {
    // Find the node to check if it's a placeholder
    const nodeToCheck = currentTrigger?.root ? findNodeById(currentTrigger.root, nodeId) : null;

    // If it's a placeholder node, delete directly without confirmation
    if (nodeToCheck?.metadata?.isPlaceholder) {
      actions.deleteNode(nodeId);
    } else {
      // For all other nodes, show confirmation dialog
      setNodeToDelete(nodeId);
      setShowDeleteNodeDialog(true);
    }
  }, [currentTrigger?.root, actions]);

  const confirmDeleteNode = useCallback(() => {
    if (nodeToDelete) {
      actions.deleteNode(nodeToDelete);
      setNodeToDelete(null);
    }
    setShowDeleteNodeDialog(false);
  }, [nodeToDelete, actions]);

  const handleDeletePath = useCallback((nodeId: string) => {
    actions.deletePath(nodeId);
  }, [actions]);

  const handleToggleEdit = useCallback(() => {
    if (readOnly) return;
    actions.setEditMode(!editorState.isEditing);
  }, [readOnly, editorState.isEditing, actions]);

  // Automation action handlers
  const handleDisableAutomation = useCallback(async () => {
    if (!automationId) {
      showErrorMessage('No automation ID found');
      return;
    }

    try {
      await disableAutomation({ variables: { automationId } });
      await refetchAutomation();
      showSuccessMessage('Automation has been disabled');
      setShowDisableDialog(false);
      if (redirectUrl) {
        history.push(redirectUrl);
      }
    } catch (error) {
      showErrorMessage('Failed to disable automation');
    }
  }, [automationId, disableAutomation, refetchAutomation, showSuccessMessage, showErrorMessage, redirectUrl, history]);

  const handleResumeAutomation = useCallback(async () => {
    if (!automationId) {
      showErrorMessage('No automation ID found');
      return;
    }

    try {
      await resumeAutomation({ variables: { automationId } });
      await refetchAutomation();
      showSuccessMessage('Automation has been resumed');
      setShowResumeDialog(false);
      if (redirectUrl) {
        history.push(redirectUrl);
      }
    } catch (error) {
      showErrorMessage('Failed to resume automation');
    }
  }, [automationId, resumeAutomation, refetchAutomation, showSuccessMessage, showErrorMessage, redirectUrl, history]);

  const handleDeleteAutomation = useCallback(async () => {
    if (!automationId) {
      showErrorMessage('No automation ID found');
      return;
    }

    try {
      await deleteAutomation({ variables: { automationId } });
      showSuccessMessage('Automation has been deleted');
      setShowDeleteDialog(false);
      if (redirectUrl) {
        history.push(redirectUrl);
      }
    } catch (error) {
      showErrorMessage('Failed to delete automation');
    }
  }, [automationId, deleteAutomation, showSuccessMessage, showErrorMessage, redirectUrl, history]);

  // Show template selection modal only when explicitly shown
  if (showTemplateSelection) {
    return (
      <TemplateSelectionModal
        isOpen
        onClose={handleCancelTemplateSelection}
        onSelectTemplate={handleTemplateSelect}
        projectId={projectId}
      />
    );
  }

  // Loading states
  if (isLoadingAutomation || isLoadingTemplate || isLoadingNodeOptions || isLoadingMessageTemplates) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-grey-5">
            {(isLoadingNodeOptions || isLoadingAutomation || isLoadingTemplate || isLoadingMessageTemplates) && 'Loading automations...'}
          </p>
        </div>
      </div>
    );
  }

  // Don't render editor until we have a trigger
  if (!currentTrigger) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-grey-5">No automation trigger found. Please select a template.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white flex flex-col">
      {/* Header */}
      <AutomationHeader
        breadcrumbs={breadcrumbs}
        currentPage={currentPage}
        onCancel={onCancel}
        redirectUrl={redirectUrl}
        automationName={editorState.blueprint.name}
        automation={automation}
        hasUnsavedChanges={editorState.hasUnsavedChanges}
        readOnly={readOnly}
        isEditing={editorState.isEditing}
        onToggleEdit={handleToggleEdit}
        onSave={actions.saveBlueprint}
        isSaving={isCreating || isUpdating}
        automationId={automationId}
        onDisable={() => setShowDisableDialog(true)}
        onResume={() => setShowResumeDialog(true)}
        onDelete={() => setShowDeleteDialog(true)}
        history={history}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Center - Flow Canvas */}
        <div className="flex-1 relative bg-grey-1">
          <ReactFlowProvider>
            <FlowCanvas
              trigger={currentTrigger}
              isEditing={editorState.isEditing}
              selectedNodeId={editorState.selectedNodeId}
              onNodeClick={handleNodeClick}
              onUpdateNode={handleUpdateNode}
              onAddChild={handleAddChild}
              onDeleteNode={handleDeleteNode}
              onDeletePath={handleDeletePath}
              actions={actions}
              automationId={automationId}
              templateId={selectedTemplateId}
              actionOptions={actionOptions}
              programData={program.data}
              isStaOffer={isStaOffer}
            />
          </ReactFlowProvider>
        </div>

        {/* Right Panel - Properties Sheet (when node selected and not a placeholder) */}
        <Sheet
          open={selectedNode && editorState.isEditing && !selectedNode.metadata?.isPlaceholder && !selectedNode.metadata?.isActionPlaceholder}
          onOpenChange={(open) => !open && actions.setSelectedNode(null)}
        >
          <SheetContent side="right" className="w-[345px] p-0" showOverlay={false}>
            {selectedNode && (
              <PropertyPanel
                selectedNode={selectedNode}
                onUpdateNode={actions.updateNode}
                onClose={() => actions.setSelectedNode(null)}
                actionOptions={actionOptions}
                conditionOptions={conditionOptions}
                automationVariables={editorState.blueprint.triggers[0]?.variables || []}
                onUpdateMessageTemplate={handleMessageTemplateUpdate}
                resetMessageTemplate={false}
                onRefreshNodeOptions={refetchNodeOptions}
                projectId={projectId}
                blueprintName={editorState.blueprint.name}
                isStaOffer={isStaOffer}
              />
            )}
          </SheetContent>
        </Sheet>

        {/* Floating Node Palette */}
        {editorState.isEditing && (
          <FloatingNodePalette
            programData={program.data}
            isStaOffer={isStaOffer}

          />
        )}
      </div>

      {/* Automation Action Dialogs */}
      {/* Disable Dialog */}
      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Automation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable this automation? This will stop the automation from running.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisableAutomation} disabled={disableLoading}>
              {disableLoading ? 'Disabling...' : 'Disable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resume Dialog */}
      <AlertDialog open={showResumeDialog} onOpenChange={setShowResumeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resume Automation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to resume this automation? This will start the automation running again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResumeAutomation} disabled={resumeLoading}>
              {resumeLoading ? 'Resuming...' : 'Resume'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Automation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this automation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAutomation}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Node Dialog */}
      <AlertDialog open={showDeleteNodeDialog} onOpenChange={setShowDeleteNodeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Node</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this node? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteNodeDialog(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteNode}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Node
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
